import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tag } from 'lucide-react';

// "For" picker shown while importing an order copy. The server parsed the
// file, found orders placed by wholesalers and wrote NOTHING; it returned
// those orders (status "retailer_tags_needed"). The user says who each
// order is for and the screen re-sends the same file with the answer.
//
// Choice values: 'own' | 'untagged' | 'keep' | 'ret:<retailer_id>' | 'new'
// (a typed name — the server re-uses an existing retailer of that name).
//
// Props: prompt = the server's payload, fileName, onDone(tags | null).
// onDone(null) = the user cancelled, so the import must not go ahead.

const initialChoice = (o, retailers) => {
  const cur = o.current;
  if (!cur) return '';                                   // a new order: the user must choose
  if (cur.kind === 'own') return 'own';
  if (cur.kind === 'untagged') return 'untagged';
  if (cur.kind === 'retailer' && cur.retailer_id
      && (retailers || []).some(r => r.retailer_id === cur.retailer_id)) return `ret:${cur.retailer_id}`;
  return 'keep';                                         // split / mixed / an inactive retailer
};

export default function ImportRetailerTagsDialog({ prompt, fileName, onDone }) {
  const orders = useMemo(() => prompt?.orders || [], [prompt]);
  const retailersOf = useMemo(() => prompt?.retailers || {}, [prompt]);
  const [choice, setChoice] = useState({});
  const [newName, setNewName] = useState({});

  useEffect(() => {
    const c = {};
    orders.forEach(o => { c[o.order_no] = initialChoice(o, retailersOf[o.customer_id]); });
    setChoice(c);
    setNewName({});
  }, [orders, retailersOf]);

  // One block per wholesaler, in the server's order (customer, then order no).
  const groups = useMemo(() => {
    const m = new Map();
    orders.forEach(o => {
      if (!m.has(o.customer_id)) m.set(o.customer_id, { customer_id: o.customer_id, name: o.customer_name, code: o.customer_code, orders: [] });
      m.get(o.customer_id).orders.push(o);
    });
    return Array.from(m.values());
  }, [orders]);

  const setOne = (ono, v) => setChoice(prev => ({ ...prev, [ono]: v }));
  // Bulk choices only fill orders that carry no tag yet — one click must
  // never wipe or overwrite tags already on file (change those one by one).
  const hasTag = (o) => !!o.current && o.current.kind !== 'untagged';
  const setMany = (list, v) => setChoice(prev => {
    const next = { ...prev };
    list.filter(o => !hasTag(o)).forEach(o => { next[o.order_no] = v; });
    return next;
  });
  const untaggedCount = orders.filter(o => !hasTag(o)).length;

  const missing = orders.filter(o => {
    const v = choice[o.order_no];
    if (!v) return true;
    if (v === 'new') return !(newName[o.order_no] || '').trim();
    return false;
  });

  const submit = () => {
    if (missing.length) return;
    const tags = {};
    orders.forEach(o => {
      const v = choice[o.order_no];
      if (v === 'own' || v === 'untagged' || v === 'keep') tags[o.order_no] = { kind: v };
      else if (v === 'new') tags[o.order_no] = { kind: 'retailer', new_name: (newName[o.order_no] || '').trim().replace(/\s+/g, ' ') };
      else if (v.startsWith('ret:')) tags[o.order_no] = { kind: 'retailer', retailer_id: v.slice(4) };
    });
    onDone(tags);
  };

  const fmtDate = (d) => {
    if (!d) return 'undated';
    const s = String(d).slice(0, 10);
    const [y, m, dd] = s.split('-');
    return y && m && dd ? `${dd}-${m}-${y}` : s;
  };

  const pickerItems = (o) => {
    const rets = retailersOf[o.customer_id] || [];
    return (
      <SelectContent>
        {o.current && o.current.kind !== 'own' && o.current.kind !== 'untagged' && (
          <SelectItem value="keep">Keep current ({o.current.name || 'tagged'})</SelectItem>
        )}
        <SelectItem value="own">Own shop</SelectItem>
        <SelectItem value="untagged">{hasTag(o) ? 'Clear the tag (not tagged)' : 'Not tagged (decide later)'}</SelectItem>
        {rets.length > 0 && (
          <SelectGroup>
            <SelectLabel className="text-[11px] font-bold text-sky-700">Retailers</SelectLabel>
            {rets.map(r => (
              <SelectItem key={r.retailer_id} value={`ret:${r.retailer_id}`}>
                {r.name}{r.alias ? ` · ${r.alias}` : ''}
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        <SelectItem value="new">+ New retailer…</SelectItem>
      </SelectContent>
    );
  };

  return (
    <Dialog open={!!prompt} onOpenChange={(open) => { if (!open) onDone(null); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-indigo-700">
            <Tag size={16} /> Who are these orders for?
          </DialogTitle>
          <DialogDescription>
            {fileName ? <span className="font-medium">{fileName}</span> : 'This file'} has {orders.length} wholesaler order{orders.length === 1 ? '' : 's'}
            {prompt?.division ? <> in {prompt.division}</> : null}. Nothing has been imported yet.
            Pick the retailer (a name or just a number), Own shop, or Not tagged. You can change it later from the Orders tab.
          </DialogDescription>
        </DialogHeader>

        {untaggedCount > 1 && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-slate-500">Every order not yet tagged ({untaggedCount}):</span>
            <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setMany(orders, 'own')}>Own shop</Button>
            <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setMany(orders, 'untagged')}>Not tagged</Button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {groups.map(g => (
            <div key={g.customer_id} className="border border-slate-200 rounded-lg">
              <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 rounded-t-lg">
                <div className="text-sm font-semibold text-slate-800">
                  {g.name} <span className="text-xs font-normal text-slate-500 font-mono">{g.code}</span>
                </div>
                {g.orders.filter(o => !hasTag(o)).length > 1 && (
                  <Select value="" onValueChange={(v) => setMany(g.orders, v)}>
                    <SelectTrigger className="h-7 text-xs w-48"><SelectValue placeholder={`All ${g.orders.filter(o => !hasTag(o)).length} untagged orders…`} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="own">Own shop</SelectItem>
                      <SelectItem value="untagged">Not tagged</SelectItem>
                      {(retailersOf[g.customer_id] || []).map(r => (
                        <SelectItem key={r.retailer_id} value={`ret:${r.retailer_id}`}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="divide-y divide-slate-100">
                {g.orders.map(o => (
                  <div key={o.order_no} className="px-3 py-2 grid grid-cols-1 sm:grid-cols-[1fr_15rem] gap-2 items-center">
                    <div className="text-xs">
                      <span className="font-mono font-semibold text-slate-800">{o.order_no}</span>
                      <span className="text-slate-500"> · {fmtDate(o.order_date)} · {o.lines} line{o.lines === 1 ? '' : 's'} · {o.units} units</span>
                      {o.current && (
                        <div className="text-[11px] text-slate-400">Already on file · now: {o.current.name}</div>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Select value={choice[o.order_no] || ''} onValueChange={(v) => setOne(o.order_no, v)}>
                        <SelectTrigger className={`h-8 text-xs ${!choice[o.order_no] ? 'border-amber-400' : ''}`}>
                          <SelectValue placeholder="Choose who it is for…" />
                        </SelectTrigger>
                        {pickerItems(o)}
                      </Select>
                      {choice[o.order_no] === 'new' && (
                        <Input autoFocus maxLength={120} className="h-8 text-xs" placeholder="Retailer name or number"
                          value={newName[o.order_no] || ''}
                          onChange={(e) => setNewName(prev => ({ ...prev, [o.order_no]: e.target.value }))} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2 pt-2 border-t border-slate-100 items-center">
          {missing.length > 0 && (
            <span className="text-xs text-amber-700 mr-auto">{missing.length} order{missing.length === 1 ? '' : 's'} still to choose</span>
          )}
          <Button variant="outline" onClick={() => onDone(null)}>Cancel import</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={submit} disabled={missing.length > 0}>
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
